<template>
    <!-- <Breadcrumbs main="Setting" mains="Terapist Management" title="Terapist Management" /> -->
    <div class="container-fluid default-dashboard">
        <div class="card">
            <div class="card-header">
                <div class="d-flex justify-content-between align-items-center">
                    <h5>Terapist List</h5>
                    <button class="btn btn-primary" @click="openModal()">
                        <i class="fa fa-plus"></i> Add Terapist
                    </button>
                </div>
            </div>
            <div class="card-body">
                <DataTable
                    :headers="headers"
                    :items="terapists"
                    :loading="loading"
                    :total="total"
                    :params="params"
                    @update:params="updateParams"
                >
                    <template #item.image="{ item }">
                        <img v-if="item.image" :src="item.image" alt="Terapist Image" style="max-width: 60px; max-height: 60px;" />
                        <span v-else>-</span>
                    </template>
                    <template #item.actions="{ item }">
                        <button class="btn btn-sm btn-warning me-2" @click="editTerapist(item as Terapist)">
                            <i class="fa fa-edit"></i>
                        </button>
                        <button class="btn btn-sm btn-danger" @click="deleteTerapist(item as Terapist)">
                            <i class="fa fa-trash"></i>
                        </button>
                    </template>
                </DataTable>
            </div>
        </div>
    </div>

    <div class="modal fade bd-example-modal-lg" id="terapistModal" tabindex="-1" ref="modalRef" aria-labelledby="terapistModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg" role="document">
            <div class="modal-content">
                <h3 class="modal-header justify-content-center border-0">{{ modalTitle }}</h3>
                    <div class="modal-body">
                        <div class="row">
                            <div class="col-md-6 mb-2">
                                <label class="form-label col-form-label">Code</label>
                                <input v-model="form.code" type="text" class="form-control" required>
                            </div>
                            <div class="col-md-6 mb-2">
                                <label class="form-label col-form-label">Name</label>
                                <input v-model="form.name" type="text" class="form-control" required>
                            </div>
                            <div class="col-md-6 mb-2">
                                <label class="form-label col-form-label">Phone</label>
                                <input v-model="form.phone" type="text" class="form-control">
                            </div>
                            <div class="col-md-6 mb-2">
                                <label class="form-label col-form-label">Address</label>
                                <input v-model.number="form.address" type="text" class="form-control">
                            </div>
                            <div class="mb-2">
                                <label class="form-label col-form-label">Image</label>
                                <input type="file" class="form-control" accept="image/*" @change="handleImageUpload">
                                <div v-if="form.image" class="mt-2">
                                    <img :src="form.image" alt="Preview" style="max-width: 200px; max-height: 200px;">
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button class="btn btn-danger" type="button" @click="closeModal">Cancel</button>
                        <button class="btn btn-primary" type="submit" @click="saveTerapist">Save</button>
                    </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, reactive, onMounted } from 'vue'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import DataTable from '@/components/table/Datatable.vue'
import { Modal } from 'bootstrap'

// {{ Define Terapist interface }}
interface Terapist {
    ID: any; // Allow null for new terapists
    code: string;
    name: string;
    phone: string;
    address: string;
    image: string; // base64 string
}

const headers = [
    { text: '#', value: 'image' },
    { text: 'Code', value: 'code' },
    { text: 'Name', value: 'name' },
    { text: 'Phone', value: 'phone' },
    { text: 'Address', value: 'address' },
    { text: 'Actions', value: 'actions' }
]

// {{ Apply types to refs and reactive objects }}
const terapists = ref<Terapist[]>([])
const loading = ref(false)
const total = ref(0)
const params = reactive<DataTableParams>({
    page: 1,
    size: 10,
    search: ''
})

const modalTitle = ref('')
const form = reactive<Terapist>({
    ID: null,
    code: '',
    name: '',
    phone: '',
    address: '',
    image: ''
})

// Add this data data array
const dataTerapist: Terapist[] = []
import * as master from "@/api/services/MasterRequest";

const fetchTerapist = async () => {
    loading.value = true
    try {
        // Simulate API delay
        const resp = await master.postRequest("/terapist/list", {...params});
        dataTerapist.splice(0, dataTerapist.length);
        dataTerapist.push(...resp.data);

        // Filter based on search
        let filteredTerapist = [...dataTerapist]
        if (params.search) {
            const searchLower = params.search.toLowerCase()
            filteredTerapist = dataTerapist.filter(terapist =>
                terapist.code.toLowerCase().includes(searchLower) ||
                terapist.name.toLowerCase().includes(searchLower)
            )
        }

        // Calculate pagination
        const start = (params.page - 1) * params.size
        const end = start + params.size
        terapists.value = filteredTerapist.slice(start, end)
        total.value = filteredTerapist.length

    } catch (error) {
        toast.error('Failed to fetch terapists')
    }
    loading.value = false
}

// {{ Add type annotation for newParams }}
const updateParams = (newParams: Partial<DataTableParams>) => {
    Object.assign(params, newParams)
    fetchTerapist()
}

const modalRef = ref<HTMLElement | null>(null) // Type the ref
let bsModal: any | null = null // Type the bootstrap modal instance as any since Modal namespace can't be used directly

// View modal state
const viewModalRef = ref<HTMLElement | null>(null)
let bsViewModal: any | null = null
const selectedTerapist = ref<Terapist | null>(null)

onMounted(() => {
    if (modalRef.value) { // Check if modalRef exists
        bsModal = new Modal(modalRef.value)
    }
    if (viewModalRef.value) {
        bsViewModal = new Modal(viewModalRef.value)
    }
})

const closeModal = () => {
    if (bsModal) { // Check if bsModal is initialized
        bsModal.hide()
    }
}

const saveTerapist = async () => {
    loading.value = true; // Indicate loading state
    try {
        // Simulate API call
        // await new Promise(resolve => setTimeout(resolve, 500));
        if (form.ID) {
            // Update existing terapist in data data
            await master.postRequest("/terapist/update", form).then((resp) => {
                if (resp.status === 200) {
                    toast.success('Terapist updated successfully');
                    closeModal();
                    fetchTerapist(); // Re-fetch to update the table view
                } else {
                    toast.error('Failed to update terapist');
                }
            }).catch((error) => {
                toast.error(error.message || 'Failed to update terapist');
            })
        } else {
            await master.postRequest("/terapist/create", form).then((resp) => {
                if (resp.status === 200) {
                    toast.success('Terapist created successfully');
                    closeModal();
                    fetchTerapist(); // Re-fetch to update the table view
                } else {
                    toast.error('Failed to create terapist');
                }
            }).catch((error) => {
                toast.error(error.message || 'Failed to create terapist');
            })
        }
    } catch (error: any) { // Type error
        toast.error(error.message || 'Failed to save terapist');
    } finally {
        loading.value = false; // End loading state
    }
}

// {{ Define editTerapist function }}
const editTerapist = (terapist: Terapist) => {
    openModal(terapist); // Call openModal with the terapist data
}


// {{ Add type annotation for terapist parameter }}
const deleteTerapist = async (terapist: Terapist) => {
    if (!confirm('Are you sure you want to delete this terapist?')) return

    loading.value = true; // Indicate loading state
    try {
        // Simulate API call
        await master.postRequest("/terapist/delete", {ID: terapist.ID}).then((resp) => {
            if (resp.status === 200) {
                toast.success('Terapist deleted successfully');
                closeModal();
                fetchTerapist(); // Re-fetch to update the table view
            } else {
                toast.error('Failed to delete terapist');
            }
        })

    } catch (error: any) { // Type error
        toast.error(error.message || 'Failed to delete terapist');
    } finally {
        loading.value = false; // End loading state
    }
}

// Update openModal to ensure accounts array exists
const openModal = (terapist: Terapist | null = null) => {
    if (terapist) {
        modalTitle.value = 'Edit Terapist'
        Object.assign(form, terapist);
        // Deep copy accounts to avoid mutating dataTerapist directly
    } else {
        modalTitle.value = 'Create Terapist'
        form.ID = null
        form.code = ''
        form.name = ''
        form.phone = ''
        form.address = ''
        form.image = ''
    }
    if (bsModal) {
        bsModal.show()
    }
}

// Image upload handler
const handleImageUpload = (event: Event) => {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files[0]) {
        const reader = new FileReader();
        reader.onload = (e) => {
            console.log(form.image)
            form.image = e.target?.result as string;
        };
        reader.readAsDataURL(target.files[0]);
    }
}
</script>